import { AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogModule,
} from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ToastrService } from 'ngx-toastr';
import {
  EMPTY,
  Observable,
  Subject,
  combineLatest,
  filter,
  map,
  of,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import { SchoolMember } from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { SchoolRoles } from '../../../../models/roles.school.model';
import {
  Session,
  SessionStatus,
} from '../../../../models/session.school.model';
import { UserModel } from '../../../../models/user.model';
import { InitialsPipe } from '../../../../pipes/initials.pipe';
import { MomentPipe } from '../../../../pipes/moment.pipe';
import { SchoolRolesService } from '../../../../services/school-roles.service';
import { UtilsService } from '../../../../services/utils.service';
import * as sessionActions from '../../../../state/actions/session.school.actions';
import { AppState } from '../../../../state/appstate';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as memberSelector from '../../../../state/selectors/member.school.selector';
import * as sessionSelector from '../../../../state/selectors/session.school.selector';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { DlgSessionAttendanceComponent } from '../dlg-session-attendance/dlg-session-attendance.component';
import { DlgUpsertSessionComponent } from '../dlg-upsert-session/dlg-upsert-session.component';

@Component({
  selector: 'app-list-sessions',
  standalone: true,
  imports: [
    AsyncPipe,
    MatDialogModule,
    BtnFabComponent,
    NgxSkeletonLoaderModule,
    MomentPipe,
    InitialsPipe,
  ],
  templateUrl: './list-sessions.component.html',
  styleUrl: './list-sessions.component.css',
})
export class ListSessionsComponent implements OnInit, OnDestroy {
  route: ActivatedRoute = inject(ActivatedRoute);
  roles: SchoolRolesService = inject(SchoolRolesService);
  utilsService: UtilsService = inject(UtilsService);
  private dialog: MatDialog = inject(MatDialog);
  toastr: ToastrService = inject(ToastrService);
  logger: NGXLogger = inject(NGXLogger);

  sessions$: Observable<Session[] | null | undefined> = new Observable();
  teacher: SchoolMember | null | undefined = undefined;

  loading$: Observable<boolean> = new Observable();
  inProgress$: Observable<boolean> = new Observable();

  groupId: string | null = this.route.root.snapshot.paramMap.get('id');
  facultyId: string | null = this.route.root.snapshot.paramMap.get('facultyId');
  gradeId: string | null = this.route.root.snapshot.paramMap.get('gradeId');
  academicYear: number | undefined = undefined;

  user: UserModel | null = null;
  group: Group | null | undefined;
  grade: Grade | null | undefined = null;

  membershipStatus = MembershipStatus;
  // memberType = SchoolMemberType;
  sessionStatus = SessionStatus;
  rolesType = SchoolRoles;
  utils = UtilsService;

  myRoles$: Observable<any> = new Observable();
  exit$: Subject<void> = new Subject<void>();

  private params$ = combineLatest({
    // Use .parent if the component is in a child router-outlet
    params: this.route.paramMap,
    // Use queryParams from the current route
    queryParams: this.route.queryParamMap,
  }).pipe(
    map(({ params, queryParams }) => {
      // If current params are empty, try to look at the parent
      let pMap = params;
      let currentRoute = this.route;

      // Walk up the tree until we find the groupId or hit the top
      while (currentRoute.parent && !pMap.has('id')) {
        currentRoute = currentRoute.parent;
        pMap = currentRoute.snapshot.paramMap;
      }

      return {
        groupId: pMap.get('id'),
        facultyId: pMap.get('facultyId'),
        gradeId: pMap.get('gradeId'),
        academicYear: queryParams.get('academicYear')
          ? Number(queryParams.get('academicYear'))
          : undefined,
      };
    }),
    takeUntil(this.exit$),
  );

  constructor(private store: Store<AppState>) {
    this.loading$ = this.store
      .pipe(select(sessionSelector.loading))
      .pipe(takeUntil(this.exit$));

    this.inProgress$ = this.store
      .pipe(select(sessionSelector.inProgress))
      .pipe(takeUntil(this.exit$));

    this.myRoles$ = this.store
      .select(groupSelector.getMyRolesInGroup(this.groupId))
      .pipe(takeUntil(this.exit$));
  }

  ngOnInit(): void {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    this.params$.subscribe(({ groupId, facultyId, gradeId, academicYear }) => {
      this.groupId = groupId;
      this.facultyId = facultyId;
      this.gradeId = gradeId;
      this.academicYear = academicYear;

      this.logger.debug(
        `ListSessionsComponent :: Received route params groupId=${groupId}, facultyId=${facultyId}, gradeId=${gradeId}, academicYear=${academicYear}`,
      );

      if (groupId) {
        this.loadData(groupId, facultyId, gradeId, academicYear);
      }
    });
  }

  private loadData(
    groupId: string,
    facultyId: string | null,
    gradeId: string | null,
    academicYear?: number,
  ) {
    // 1. Reset local state immediately to prevent "Ghost" data from previous users
    this.teacher = undefined;
    this.grade = null;
    this.sessions$ = of([]);

    this.logger.debug(
      `ListSessionsComponent :: Loading data for groupId=${groupId}`,
    );

    this.store
      .select(groupSelector.getGroupById(groupId))
      .pipe(
        take(1),
        // Use switchMap to ensure that if loadData is called again,
        // the previous internal logic is cancelled.
        switchMap((group) => {
          this.group = group;
          const year = academicYear ?? group?.academicYear;
          this.academicYear = year;

          if (!year) return EMPTY;

          // Determine which ID to use for fetching
          if (gradeId) {
            return of({ gradeId, year, groupId });
          } else if (facultyId) {
            // Find the teacher first to get their gradeId
            return this.store
              .select(memberSelector.getSchoolMemberById(facultyId, groupId))
              .pipe(
                filter((m) => !!m),
                take(1),
                tap((t) => (this.teacher = t)),
                map((t) => ({ gradeId: t!.gradeId!, year, groupId })),
              );
          }
          return EMPTY;
        }),
        takeUntil(this.exit$),
      )
      .subscribe(({ gradeId, year, groupId }) => {
        if (gradeId) {
          this.executeDataFetch(groupId, gradeId, year);
        }
      });
  }

  private executeDataFetch(groupId: string, gradeId: string, year: number) {
    // 1. Setup Sessions Stream
    this.sessions$ = this.store.select(
      sessionSelector.getSessionsByGradeId(gradeId, groupId, year),
    );

    // 2. Load Grade (One-time fetch or live stream)
    this.store
      .select(
        gradeSelector.getGradeById({ gradeId, groupId, academicYear: year }),
      )
      .pipe(takeUntil(this.exit$))
      .subscribe((grade) => {
        this.grade = grade;
        // Update teacher if the grade object provides a new one
        if (grade?.teacherId) {
          this.store
            .select(
              memberSelector.getSchoolMemberById(grade.teacherId, groupId),
            )
            .pipe(take(1))
            .subscribe((t) => (this.teacher = t));
        }
      });

    // 3. Dispatch action if store is empty
    this.sessions$.pipe(take(1)).subscribe((sessions) => {
      if (!sessions || sessions.length === 0) {
        this.store.dispatch(
          sessionActions.getSessions({
            groupId,
            gradeId,
            academicYear: year,
            status: [SessionStatus.active, SessionStatus.closed],
          }),
        );
      }
    });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  addMore() {
    const activeGradeId = this.gradeId ?? this.teacher?.gradeId;
    this.sessions$.pipe(take(1)).subscribe((sessions) => {
      if (this.groupId && activeGradeId && this.academicYear) {
        this.store.dispatch(
          sessionActions.getSessions({
            groupId: this.groupId,
            gradeId: activeGradeId,
            academicYear: this.academicYear,
            // sessions are ordered by startDate desc: page on that field
            lastRecord:
              (sessions ?? [])?.length > 0
                ? sessions![sessions!.length - 1]?.startDate
                : undefined,
          }),
        );
      }
    });
  }

  onRefresh() {
    const activeGradeId = this.gradeId ?? this.teacher?.gradeId;
    if (this.groupId && activeGradeId && this.academicYear) {
      this.store.dispatch(
        sessionActions.getSessions({
          groupId: this.groupId,
          gradeId: activeGradeId,
          academicYear: this.academicYear,
        }),
      );
    }
  }

  /**
   * Opens a dialog to take attendance for a specific session.
   * @param item The session for which attendance is to be taken.
   */
  onSessionAttendance(item: Session) {
    if (
      item.id === undefined ||
      !this.grade?.id ||
      !this.groupId ||
      !(this.academicYear ?? this.group?.academicYear)
    ) {
      // Same year fallback as the dialog data below, so past-year views work.
      this.logger.error(
        'ListSessionsComponent :: onSessionAttendance: required data missing',
        { sessionId: item.id, gradeId: this.grade?.id, groupId: this.groupId },
      );
      this.utilsService.onShowAlert({
        message: 'Required data missing. Kindly login again',
        title: 'Error taking Attendance',
      });
      return;
    }

    let dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = ['!p-0', '!bg-transparent', '!shadow-none'];

    dialogConfig.data = {
      title: 'Update Attendance',
      description: `Update attendance for session '${item.sessionName}'`,
      action: 'Update',
      groupId: this.groupId,
      academicYear: this.academicYear ?? this.group?.academicYear,
      sessionId: item.id,
      sessionStart: item.startDate,
      gradeId: this.grade?.id,
    };

    const dialogRef = this.dialog.open(
      DlgSessionAttendanceComponent,
      dialogConfig,
    );

    return dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const session: Session = {
          sessionStatus: SessionStatus.active,
        } as Session;

        return this.store.dispatch(
          sessionActions.update({
            groupId: this.groupId!,
            sessionId: item.id!,
            session: session,
          }),
        );
      }
      return;
    });
  }

  onEditSession(item: Session) {
    let dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = ['!p-0', '!bg-transparent', '!shadow-none'];

    dialogConfig.data = {
      title: 'Update Session',
      description: `Update session for '${this.grade?.gradeName ?? item.gradeName ?? ''}' grade`,
      action: 'Update',
      sessionId: item.id,
      session: item,
      sessionName: item.sessionName,
      startDate: item.startDate.toDate(),
      endDate: item.endDate.toDate(),
      defaultTeacherId: item.teacherId,
      defaultTeacherName: item.teacherName,
      defaultTeacherPhotoUrl: item.teacherPhotoUrl,
      defaultLessonId: item.lessonId,
      defaultLessonName: item.lessonName,
      groupId: this.groupId ?? item.groupId,
      grade: this.grade,
      gradeId: this.grade?.id ?? item.gradeId,
      gradeName: this.grade?.gradeName ?? item.gradeName,
      academicYear:
        this.academicYear ?? this.group?.academicYear ?? item.academicYear,
      user: this.user,
      faculty: this.teacher,
      isAssignable: true,
    };

    const dialogRef = this.dialog.open(DlgUpsertSessionComponent, dialogConfig);
    return dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.toastr.success('Session updated successfully', '', {
          timeOut: 3000,
        });
      }
    });
  }

  onDeleteSession(item: Session) {
    let dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = ['!p-0', '!bg-transparent', '!shadow-none'];

    dialogConfig.data = {
      title: 'Delete Session',
      description: `Are you sure you want to delete session '${item.sessionName}'?`,
      action: 'Delete',
      buttonColor: 'bg-red-500',
      // showType: 'warning',
    };

    const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);

    return dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result && item.groupId) {
        return this.store.dispatch(
          sessionActions.deleteAction({ groupId: item.groupId, id: item.id! }),
        );
      }
      return;
    });
  }
}
